import axios from 'axios';
import { Courses, ListPageModel } from '../../auth';

const API_ASSEMP_URL = `${process.env.REACT_APP_ASSEMP_URL}/nucleo-ms/api`;

const coursesService = {
    /**
     * Fetch all courses
     * @returns {Promise<Array>} List of courses
     */
    getCourses: async (): Promise<ListPageModel<Courses>> => {
        try {
            const response = await axios.get<ListPageModel<Courses>>(`${API_ASSEMP_URL}/cursos/find?status=true`, {
                // Params de paginação podem ser adicionados aqui
                // params: {
                //   size: 50
                // }
            });
            return response.data; // Já retorna no formato correto
        } catch (error) {
            console.error('Error fetching courses:', error);
            throw error;
        }
    },

    getFindInCourses: async (queryString): Promise<ListPageModel<Courses>> => {
        try {
            const response = await axios.get<ListPageModel<Courses>>(`${API_ASSEMP_URL}/cursos/find?${queryString}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching courses:', error);
            throw error;
        }
    },

    /**
     * Fetch a course by ID
     * @param {string | number} id - Course ID
     * @returns {Promise<Object>} Course data
     */
    getCourseById: async (id) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching course with ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * Create a new course
     * @param {Object} courseData - Data of the new course
     * @returns {Promise<Object>} Created course data
     */
    createCourse: async (courseData) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/cursos`, courseData);
            return response.data;
        } catch (error) {
            console.error('Error creating course:', error);
            throw error;
        }
    },

    /**
     * Update an existing course
     * @param {string | number} id - Course ID
     * @param {Object} courseData - Updated course data
     * @returns {Promise<Object>} Updated course data
     */
    updateCourse: async (id, courseData) => {
        try {
            const response = await axios.patch(`${API_ASSEMP_URL}/cursos`, courseData);
            return response.data;
        } catch (error) {
            console.error(`Error updating course with ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a course
     * @param {string | number} id - Course ID
     * @returns {Promise<void>} Confirmation of deletion
     */
    deleteCourse: async (id) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/cursos/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting course with ID ${id}:`, error);
            throw error;
        }
    },

    /**
   * Attendance student to class in course
   * @param {string | number} id - Course ID
   * @returns {Promise<void>} Add student to class attendance
   */
    attendanceStudent: async (courseData) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/cursos/chamada`, courseData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },



    /**
 * Fetch all available class dates for a course by month
 * @param {string | number} idCurso - Course ID
 * @param {string} mes - Month number (optional)
 * @returns {Promise<Array>} List of available dates
 */
    getCourseDatesByMonth: async (idCurso, mes = "2") => {
        try {
            const url = `${API_ASSEMP_URL}/cursos/${idCurso}/datas${mes ? `?mes=${mes}` : ""}`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error(`Erro ao buscar datas do curso ${idCurso}:`, error);
            throw error;
        }
    },

    updateCourseDetails: async (formData) => {
        try {
            const url = `${API_ASSEMP_URL}/cursos`;
            const response = await axios.patch(url, formData);
            return response.data;
        } catch (error) {
            console.error(`Erro ao atualizar curso`, error);
            throw error;
        }
    },

    updateCourseValues: async (formData) => {
        try {
            const url = `${API_ASSEMP_URL}/cursos/valores`;
            const response = await axios.patch(url, formData);
            return response.data;
        } catch (error) {
            console.error(`Erro ao atualizar curso`, error);
            throw error;
        }
    },

    /**
     * Fetch holiday data
     */
    getFeriado: async () => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/feriado`);
            return response.data;
        } catch (error) {
            console.error('Error fetching holiday data:', error);
            throw error;
        }
    },

    getAllProfessors: async (): Promise<any[]> => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/professor`);
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar professores:", error);
            return [];
        }
    },

    getCursosByProfessor: async (professorId: number, pagination?: { page: number; size: number }) => {
        try {
            const params = {
                idProfessor: professorId,
                page: pagination?.page ?? 0,
                size: pagination?.size ?? 10,
            };

            const response = await axios.get(`${API_ASSEMP_URL}/cursos/find`, { params });
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar cursos do professor:", error);
            return null;
        }
    },

    getStudentCourses: async (studentId: number, cursoAtivo?: boolean, pagination?: { page: number; size: number; sort?: string[] }) => {
        try {
            const params: any = { idAluno: studentId };
            
            if (cursoAtivo !== undefined) {
                params.status = cursoAtivo;
            }
            
            if (pagination) {
                params['paginacao.page'] = pagination.page;
                params['paginacao.size'] = pagination.size;
                if (pagination.sort) {
                    params['paginacao.sort'] = pagination.sort;
                }
            }
            
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/aluno/find`, { params });
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar cursos do aluno:", error);
            return [];
        }
    },

    getAttendanceReportByMonth: async (courseId: string, numeroMes: string) => {
        try {
            const response = await axios.post(
                `${API_ASSEMP_URL}/cursos/chamada/relatorio`,
                {
                    idCurso: courseId,
                    numeroMes,
                },
                {
                    responseType: 'blob',
                }
            );
            return response;
        } catch (error) {
            console.error("Erro ao buscar relatório de chamada:", error);
            throw error;
        }
    },    
    
    getAttendanceByDate: async (courseId: string, dataAula: string) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/chamada`, {
                params: { idCurso: courseId, dataAula },
            });
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar chamada:", error);
            throw error;
        }
    },

    /**
     * Fetch students enrolled until a specific date (by filtering via dataMatricula)
     * @param {string} courseId - The course ID
     * @param {string} selectedDate - The date to filter students by enrollment date
     * @returns {Promise<Array>} List of students enrolled until the selected date
     */
    getStudentsByEnrollmentDate: async (courseId: string, selectedDate: string): Promise<any[]> => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/${courseId}/find`, {
                params: { 
                    dateMatricula: selectedDate, // Assumindo que a API tenha um parâmetro para filtrar por data de matrícula
                },
            });

            return response.data; // Retorna a lista de alunos filtrados pela data
        } catch (error) {
            console.error("Erro ao buscar alunos matriculados até a data:", error);
            throw error; // Lança erro para ser tratado onde a função for chamada
        }
    },

    getEsperaCurso: async (idCurso: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/espera/curso/${idCurso}`);
            return response.data;
        } catch (error) {
            console.log("Erro ao buscar lista de espera:", error);
            return [];
        }
    },

    
    addToWaitingList: async (idAluno: string, idCurso: string, observacao: string) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/cursos/espera`, {
                idAluno,
                idCurso,
                // observacao,
            });
            return response.data;
        } catch (error) {
            console.error("Erro ao adicionar aluno na lista de espera:", error);
            throw error;
        }
    },

    getWaitingListByCourseId: async (idCurso: string) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/espera/curso/${idCurso}`);
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar lista de espera:", error);
            throw error;
        }
    },

    getWaitingAllCourses: async () => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/cursos/espera/total`);
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar lista de espera:", error);
            throw error;
        }
    },


    removeFromWaitingList: async (idEspera: string) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/cursos/espera/${idEspera}`);
            return response.data;
        } catch (error) {
            console.error("Erro ao remover aluno da lista de espera:", error);
            throw error;
        }
    },

    addToCourseFromWaitingList: async (idEspera: string) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/cursos/espera/matricula/${idEspera}`);
            return response.data;
        } catch (error) {
            console.error("Erro ao adicionar aluno no curso a partir da lista de espera:", error);
            throw error;
        }
    },

    directEnrollment: async (idAluno: string, idCurso: string) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/cursos/matricula/direta`, {
                idAluno,
                idCurso,
            });
            return response.data;
        } catch (error) {
            console.error("Erro ao realizar matrícula direta:", error);
            throw error;
        }
    },

    removeStudentFromCourse: async (idMatricula: number) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/cursos/matricula/${idMatricula}`);
            return response.data;
        } catch (error) {
            console.error("Erro ao remover aluno do curso:", error);
            throw error;
        }
    },

    changeStudentClass: async (oldCourseId: number, studentId: number, newCourseId: number) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/cursos/matricula/trocarDeCurso`, {
                idAluno: studentId,
                idAntigoCurso: oldCourseId,
                idNovoCurso: newCourseId
            })
            
            return response.data
        } catch (error) {
            console.error("Erro ao trocar aluno de curso:", error);
            throw error;
        }
    },

    removeStudentsListFromCourse: async (array) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/cursos/matricula/remover-lista`, 
                {data: {idsMatricula: array}}
            );
            return response.data;
        } catch (error) {
            console.error("Erro ao remover aluno do curso:", error);
            throw error;
        }
    },

    
    removeStudentsListFromCourseWaitingList: async (array) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/cursos/espera`, 
                {data : {idsEspera: array}}
            );
            return response.data;
        } catch (error) {
            console.error("Erro ao remover aluno do curso:", error);
            throw error;
        }
    },

    getAllParticipants: async (nome?: string, codemp?: string, matricula?: string, queryParams?: any) => {
        try {
            const response = await axios.get<ListPageModel<any>>(`${API_ASSEMP_URL}/participante?ativo=true${queryParams ? queryParams : ''}`, {
                params: {
                    nome: nome,
                    codemp: codemp,
                    matricula: matricula
                }
            })
            return response.data;
        } catch (error) {
            console.log("🚀 ~ getAllParticipants: ~ error:", error)

        }
    },

     getStudentHistory: async (studentId?: number) => {
        try {
            const response = await axios.get<any>(`${API_ASSEMP_URL}/participante/cursosInscritos/${studentId}`)
            return response.data;
        } catch (error) {
            console.log("🚀 ~ getAllParticipants: ~ error:", error)

        }
    },
};

export default coursesService;
